import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import DeeperAnalysisDropdown from '../components/DeeperAnalysisDropdown'

export default function RecentSearches() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [analyses, setAnalyses] = useState([])
  const [expandedId, setExpandedId] = useState(null)
  const analysisId = searchParams.get('analysis_id')
  const [showSaveModal, setShowSaveModal] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [analysisToSave, setAnalysisToSave] = useState(null)
  const [saveMessage, setSaveMessage] = useState('')

  useEffect(() => {
    checkAuth()
  }, [])

  async function checkAuth() {
    try {
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        navigate('/login', { replace: true })
        return
      }

      setUser(session.user)
      await loadUserProfile(session.user.id)
      await loadAnalyses(session.user.id)
      setLoading(false)
    } catch (error) {
      console.error('Auth error:', error)
      navigate('/login', { replace: true })
    }
  }

  async function loadUserProfile(userId) {
    try {
      console.log('[RECENT] Loading profile for user:', userId)
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle()

      if (error) {
        console.error('[RECENT] Error loading profile:', error)
        return
      }

      if (data) {
        console.log('[RECENT] Profile loaded:', { role: data.role, id: data.id })
        setUserProfile(data)
      } else {
        console.warn('[RECENT] No profile found for user:', userId)
      }
    } catch (err) {
      console.error('[RECENT] Unexpected error loading profile:', err)
    }
  }

  async function loadAnalyses(userId) {
    try {
      console.log('[RECENT] Loading analyses for user:', userId)
      const { data, error } = await supabase
        .from('analyses')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(20)

      if (error) {
        console.error('[RECENT] Error loading analyses:', error)
        setAnalyses([])
      } else {
        console.log('[RECENT] Loaded analyses:', data?.length || 0)
        setAnalyses(data || [])
        if (analysisId && data) {
          const found = data.find(a => a.id === analysisId)
          if (found) setExpandedId(analysisId)
        }
      }
    } catch (err) {
      console.error('[RECENT] Unexpected error loading analyses:', err)
      setAnalyses([])
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut()
    navigate('/')
  }

  async function handleSaveSearch(analysis) {
    setAnalysisToSave(analysis)
    const topCompany = analysis.result_json?.companies?.[0]?.name || 'Search'
    const date = new Date(analysis.created_at).toLocaleDateString()
    setSaveName(`${topCompany} - ${date}`)
    setShowSaveModal(true)
  }

  async function confirmSaveSearch() {
    if (!saveName.trim() || !analysisToSave) return

    try {
      const { data: existing, error: checkError } = await supabase
        .from('saved_searches')
        .select('id')
        .eq('user_id', user.id)
        .eq('original_analysis_id', analysisToSave.id)
        .maybeSingle()

      if (checkError) throw checkError

      if (existing) {
        setSaveMessage('This analysis is already saved!')
        setTimeout(() => {
          setSaveMessage('')
          setShowSaveModal(false)
          setSaveName('')
          setAnalysisToSave(null)
        }, 2000)
        return
      }

      const { error } = await supabase
        .from('saved_searches')
        .insert({
          user_id: user.id,
          search_name: saveName,
          job_text: analysisToSave.job_text,
          result_json: analysisToSave.result_json,
          saved_from: 'recent',
          original_analysis_id: analysisToSave.id,
          is_duplicate: false
        })

      if (error) throw error

      setSaveMessage('Search saved successfully!')
      setTimeout(() => {
        setSaveMessage('')
        setShowSaveModal(false)
        setSaveName('')
        setAnalysisToSave(null)
      }, 1500)
    } catch (error) {
      console.error('Error saving search:', error)
      setSaveMessage('Error saving search. Please try again.')
      setTimeout(() => setSaveMessage(''), 3000)
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: '#98FB98', fontSize: '14px' }}>Loading recent searches...</p>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: '#98FB98', fontSize: '14px' }}>Authenticating...</p>
      </div>
    )
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      <div className="nav">
        <div className="nav-content">
          <div
            className="nav-title"
            onClick={() => navigate('/')}
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px' }}
          >
            <span>&gt; AgencySniper</span>
          </div>
          <div className="nav-actions">
            <button onClick={() => navigate('/dashboard')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Dashboard
            </button>
            <button onClick={() => navigate('/saved')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Saved
            </button>
            <button onClick={() => navigate('/agencies')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Agencies
            </button>
            {userProfile?.role === 'admin' && (
              <button onClick={() => navigate('/admin')} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
                Admin
              </button>
            )}
            <button onClick={handleSignOut} className="btn-secondary" style={{ padding: '10px 20px', fontSize: '14px' }}>
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="card">
          <h2 style={{ fontSize: '32px', marginBottom: '16px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
            &gt; Recent Analyses
          </h2>
          <p style={{ color: 'rgba(152, 251, 152, 0.85)', marginBottom: '28px', fontSize: '15px', textShadow: '0 0 5px rgba(152, 251, 152, 0.4)' }}>
            Detailed view of all job posting analyses
          </p>

          {analyses.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: 'rgba(152, 251, 152, 0.65)' }}>
              <p style={{ fontSize: '18px', marginBottom: '12px' }}>No analyses found</p>
              <p style={{ fontSize: '14px' }}>
                Run your first analysis from the dashboard
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {analyses.map((analysis) => {
                const companies = analysis.result_json?.companies || []
                const topCompany = companies.length > 0 ? companies[0] : null
                const citations = analysis.result_json?.citations || []
                const open = expandedId === analysis.id;

                return (
                  <div
                    key={analysis.id}
                    className="result-item"
                    style={{
                      border: open ? '2px solid #98FB98' : '2px solid rgba(152, 251, 152, 0.3)',
                      boxShadow: open ? '0 10px 20px rgba(34, 139, 34, 0.4), 0 0 30px rgba(152, 251, 152, 0.6)' : '0 6px 12px rgba(34, 139, 34, 0.2), 0 0 20px rgba(152, 251, 152, 0.3)'
                    }}
                  >
                    <div>
                      <button
                        onClick={() => setExpandedId(open ? null : analysis.id)}
                        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                          <div style={{ flex: 1, textAlign: 'left' }}>
                            <div style={{ fontSize: '12px', color: 'rgba(200, 200, 200, 0.8)', marginBottom: '8px', fontFamily: 'VT323, monospace' }}>
                              {new Date(analysis.created_at).toLocaleString()}
                            </div>
                            {topCompany && (
                              <div style={{ fontSize: '20px', fontWeight: '700', color: '#f5f5f5', marginBottom: '6px' }}>
                                {topCompany.name}
                              </div>
                            )}
                            <div style={{ fontSize: '14px', color: '#d0d0d0', lineHeight: '1.6' }}>
                              {analysis.job_text.substring(0, 150)}...
                            </div>
                          </div>
                          <div style={{ marginLeft: '16px', display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                            <span className={`badge ${analysis.confidence_score >= 4 ? 'badge-high' : analysis.confidence_score >= 3 ? 'badge-medium' : 'badge-low'}`}>
                              {analysis.confidence_score}/5
                            </span>
                            <div style={{
                              fontSize: '18px',
                              color: '#98FB98',
                              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.3s ease'
                            }}>
                              ▼
                            </div>
                          </div>
                        </div>
                      </button>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSaveSearch(analysis)
                          }}
                          className="btn-secondary"
                          style={{ padding: '8px 16px', fontSize: '13px', flex: 1 }}
                        >
                          ⭐ Save This Search
                        </button>
                      </div>
                    </div>

                    {open && (
                          <div style={{
                            marginTop: '20px',
                            padding: '20px',
                            background: '#2a2a2a',
                            borderRadius: '12px',
                            border: '2px solid rgba(152, 251, 152, 0.3)',
                            boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)'
                          }}>
                            <h4 style={{ fontSize: '18px', marginBottom: '16px', color: '#98FB98', fontWeight: '700' }}>
                              &gt; Full Job Description
                            </h4>
                            <p style={{ fontSize: '14px', color: '#f5f5f5', lineHeight: '1.8', marginBottom: '24px', whiteSpace: 'pre-wrap', fontFamily: 'Space Mono, Cousine, monospace' }}>
                              {analysis.job_text}
                            </p>

                            {topCompany && (
                              <div style={{ marginBottom: '24px' }}>
                                <h4 style={{ fontSize: '18px', marginBottom: '16px', color: '#98FB98', fontWeight: '700' }}>
                                  &gt; Top Match Details
                                </h4>
                                <div style={{
                                  padding: '16px',
                                  background: '#333',
                                  borderRadius: '12px',
                                  border: '2px solid rgba(152, 251, 152, 0.3)',
                                  boxShadow: '0 0 15px rgba(0, 0, 0, 0.3)'
                                }}>
                                  <div style={{ fontSize: '22px', fontWeight: '700', color: '#f5f5f5', marginBottom: '8px' }}>
                                    {topCompany.name}
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#d0d0d0', marginBottom: '12px', lineHeight: '1.7' }}>
                                    {topCompany.reasoning}
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '12px', marginTop: '16px' }}>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#98FB98' }}>
                                        {topCompany.locationMatch}/5
                                      </div>
                                      <div style={{ fontSize: '11px', color: '#d0d0d0', marginTop: '4px', fontFamily: 'VT323, monospace' }}>Location</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#98FB98' }}>
                                        {topCompany.techMatch}/5
                                      </div>
                                      <div style={{ fontSize: '11px', color: '#d0d0d0', marginTop: '4px', fontFamily: 'VT323, monospace' }}>Tech Stack</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#98FB98' }}>
                                        {topCompany.businessFit}/5
                                      </div>
                                      <div style={{ fontSize: '11px', color: '#d0d0d0', marginTop: '4px', fontFamily: 'VT323, monospace' }}>Business Fit</div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <div style={{ fontSize: '20px', fontWeight: '700', color: '#98FB98' }}>
                                        {topCompany.hiringPattern}/5
                                      </div>
                                      <div style={{ fontSize: '11px', color: '#d0d0d0', marginTop: '4px', fontFamily: 'VT323, monospace' }}>Hiring</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {companies.length > 1 && (
                              <div style={{ marginBottom: '24px' }}>
                                <h4 style={{ fontSize: '18px', marginBottom: '16px', color: '#98FB98', fontWeight: '700' }}>
                                  &gt; All Candidates ({companies.length})
                                </h4>
                                {companies.map((company, idx) => (
                                  <div
                                    key={idx}
                                    style={{
                                      padding: '12px',
                                      background: '#333',
                                      borderRadius: '12px',
                                      border: '2px solid rgba(152, 251, 152, 0.3)',
                                      marginBottom: '12px',
                                      boxShadow: '0 0 10px rgba(0, 0, 0, 0.3)'
                                    }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#f5f5f5' }}>
                                        {company.name}
                                      </div>
                                      <span className={`badge ${company.confidence > 70 ? 'badge-high' : company.confidence > 40 ? 'badge-medium' : 'badge-low'}`}>
                                        {company.confidence}%
                                      </span>
                                    </div>
                                    <div style={{ fontSize: '13px', color: '#d0d0d0', lineHeight: '1.6' }}>
                                      {company.reasoning}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {citations.length > 0 && (
                              <div>
                                <h4 style={{ fontSize: '18px', marginBottom: '16px', color: '#98FB98', fontWeight: '700' }}>
                                  &gt; Sources & Citations
                                </h4>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {citations.map((citation, idx) => (
                                    <a
                                      key={idx}
                                      href={citation}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: '#98FB98',
                                        fontSize: '13px',
                                        textDecoration: 'underline',
                                        textDecorationColor: 'rgba(152, 251, 152, 0.5)',
                                        wordBreak: 'break-all',
                                        transition: 'all 0.2s ease'
                                      }}
                                    >
                                      [{idx + 1}] {citation}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}

                            <DeeperAnalysisDropdown
                              analysisId={analysis.id}
                              supabase={supabase}
                            />

                            <div style={{ marginTop: '24px', padding: '12px', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '12px', border: '2px solid rgba(152, 251, 152, 0.2)' }}>
                              <details>
                                <summary style={{ cursor: 'pointer', color: '#98FB98', fontSize: '14px', fontWeight: '700' }}>
                                  &gt; View Full JSON Data
                                </summary>
                                <pre style={{
                                  marginTop: '12px',
                                  padding: '12px',
                                  background: 'rgba(0, 0, 0, 0.5)',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  color: '#d0d0d0',
                                  overflow: 'auto',
                                  maxHeight: '400px',
                                  fontFamily: 'Space Mono, Cousine, monospace',
                                  lineHeight: '1.5'
                                }}>
                                  {JSON.stringify(analysis.result_json, null, 2)}
                                </pre>
                              </details>
                            </div>
                          </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {showSaveModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div className="card" style={{ maxWidth: '500px', width: '100%', margin: 0 }}>
            <h3 style={{ fontSize: '24px', marginBottom: '20px', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
              &gt; Save This Search
            </h3>
            {saveMessage ? (
              <div style={{
                padding: '16px',
                background: saveMessage.includes('Error') || saveMessage.includes('already') ? 'rgba(139, 34, 34, 0.3)' : 'rgba(34, 139, 34, 0.3)',
                border: `2px solid ${saveMessage.includes('Error') || saveMessage.includes('already') ? '#FF6B6B' : '#98FB98'}`,
                borderRadius: '8px',
                color: saveMessage.includes('Error') || saveMessage.includes('already') ? '#FF6B6B' : '#98FB98',
                fontSize: '15px',
                fontWeight: '600',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                {saveMessage}
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <label className="label">Search Name</label>
                  <input
                    type="text"
                    className="input"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder="e.g., Senior Developer - Boston"
                    autoFocus
                  />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => {
                      setShowSaveModal(false)
                      setSaveName('')
                      setAnalysisToSave(null)
                      setSaveMessage('')
                    }}
                    className="btn-secondary"
                    style={{ flex: 1 }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmSaveSearch}
                    className="btn"
                    style={{ flex: 1 }}
                    disabled={!saveName.trim()}
                  >
                    Save
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
